import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Alert,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Business,
  LocalShipping,
  Store,
  Restaurant,
  Factory,
  Build,
  VolunteerActivism,
  Phone,
  Email,
  LocationOn,
  Handshake,
  Favorite,
} from '@mui/icons-material';
import ContactTiles from '../components/shared/ContactTiles';
import { useNavigate } from 'react-router-dom';

const SponsorsPage: React.FC = () => {
  const navigate = useNavigate();

  const handleNavigateToVolunteer = () => {
    navigate('/volunteer');
  };

  const sponsorCategories = [
    {
      title: 'Government & Council',
      icon: <Business sx={{ fontSize: 40, color: 'primary.main' }} />,
      sponsors: [
        'Councillor Cameron Caldwell',
        'Gold Coast City Council',
      ]
    },
    {
      title: 'Major Retailers',
      icon: <Store sx={{ fontSize: 40, color: 'primary.main' }} />,
      sponsors: [
        'Coles',
        'Woolworths',
      ]
    },
    {
      title: 'Food Organizations',
      icon: <Restaurant sx={{ fontSize: 40, color: 'primary.main' }} />,
      sponsors: [
        'OzHarvest',
        'Secondbite',
        'Foodbank QLD',
        'Made Brands PTY LTD',
      ]
    },
    {
      title: 'Local Food Suppliers',
      icon: <LocalShipping sx={{ fontSize: 40, color: 'primary.main' }} />,
      sponsors: [
        'Gold Coast Meats Nerang',
        'Local Fruit Markets',
        'Local Meat and Dry Product Wholesalers',
      ]
    },
    {
      title: 'Community Organizations',
      icon: <VolunteerActivism sx={{ fontSize: 40, color: 'primary.main' }} />,
      sponsors: [
        'Rotary Club Coomera River',
        'Rotary Club Runaway Bay',
        'Lions Club Runaway Bay',
        'Helensvale Bowls Club',
        'Bendigo Bank',
      ]
    },
    {
      title: 'Employment Services',
      icon: <Business sx={{ fontSize: 40, color: 'primary.main' }} />,
      sponsors: [
        'Tursa Employment',
        'Nortec Employment',
      ]
    },
    {
      title: 'Automotive & Mechanical',
      icon: <Build sx={{ fontSize: 40, color: 'primary.main' }} />,
      sponsors: [
        'Camping Plus Labrador',
        'Freedom Forklifts Nerang',
        'Direct Automotives – Pine Ridge Road Labrador',
        'K. Graham Mechanical Labrador',
        'Gibbs Trucks',
        'Super Cheap',
        'Repco',
        'Tyrepower Labrador',
      ]
    },
    {
      title: 'Service Providers',
      icon: <Factory sx={{ fontSize: 40, color: 'primary.main' }} />,
      sponsors: [
        'Firepower Fire Services',
        'Phil\'s Carpet and Pest Control',
        'Aaron\'s Roofing',
      ]
    },
    {
      title: 'Our Volunteers',
      icon: <Favorite sx={{ fontSize: 40, color: 'primary.main' }} />,
      sponsors: [
        'All our volunteers',
      ]
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Hero Section */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Our Sponsors
        </Typography>
        <Typography variant="h5" color="text.secondary" paragraph sx={{ maxWidth: 800, mx: 'auto' }}>
          Supporting our mission to serve the Gold Coast community
        </Typography>
        <Typography variant="body1" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
          New Life Foodbarn has a small number of sponsors which we are grateful for. 
          Donations of food from organisations is a great help, other companies allow us to buy at a reduced cost which we are thankful for.
        </Typography>
      </Box>

      {/* Introduction Section */}
      <Box sx={{ mb: 6 }}>
        <Card sx={{ p: 4 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              🤝 How Our Sponsors Help
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" paragraph>
                  We have fruiters, markets, farmers, bakery, select number of supermarkets, butchers, 
                  milk factories, general wholesalers, machinery shops, tradespersons and papers that 
                  all help this community service.
                </Typography>
                <Typography variant="body1" paragraph>
                  Foodbarn is self-funded so any source of support is a great benefit for our cause 
                  as a not-for-profit charity.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Looking for Partners:</strong> We are always on the lookout for suppliers 
                    and products to source and buy.
                  </Typography>
                </Alert>
                <Typography variant="body1">
                  Your support helps us provide affordable food to thousands of people in our community 
                  each month, including those experiencing financial difficulties, pensioners, and 
                  health care and senior card holders.
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Sponsor Categories */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 4 }}>
          Our Valued Sponsors
        </Typography>
        <Grid container spacing={4}>
          {sponsorCategories.map((category, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card sx={{ height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    {category.icon}
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                      {category.title}
                    </Typography>
                  </Box>
                  <List dense>
                    {category.sponsors.map((sponsor, sponsorIndex) => (
                      <ListItem key={sponsorIndex} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Handshake sx={{ fontSize: 16, color: 'primary.main' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={sponsor}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Impact Section */}
      <Box sx={{ mb: 6 }}>
        <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h4" component="h2" gutterBottom>
              🎯 The Impact of Your Support
            </Typography>
            <Typography variant="h6" paragraph>
              Together with our sponsors, we make a real difference in our community
            </Typography>
            <Grid container spacing={4} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                  17+
                </Typography>
                <Typography variant="body1">
                  Charities Supported
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                  1000s
                </Typography>
                <Typography variant="body1">
                  People Fed Monthly
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                  25-30k
                </Typography>
                <Typography variant="body1">
                  Tons Saved from Landfill
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                  15-25
                </Typography>
                <Typography variant="body1">
                  Daily Volunteers
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Become a Sponsor */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 4 }}>
          🤝 Become a Sponsor
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom>
                  How You Can Help
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <LocalShipping color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Food Donations"
                      secondary="Donate surplus food items to help feed our community"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Store color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Reduced Cost Purchases"
                      secondary="Allow us to purchase products at wholesale or reduced rates"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Build color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Service Support"
                      secondary="Provide services or equipment at reduced rates"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <VolunteerActivism color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Volunteer Support"
                      secondary="Encourage staff to volunteer or support our programs"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom>
                  Benefits of Sponsorship
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Favorite color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Community Impact"
                      secondary="Make a real difference in thousands of lives"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Business color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Corporate Social Responsibility"
                      secondary="Demonstrate your commitment to community welfare"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Handshake color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Recognition"
                      secondary="Be acknowledged as a valued community partner"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <VolunteerActivism color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Employee Engagement"
                      secondary="Provide meaningful volunteer opportunities for your team"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Volunteer Section */}
      <Box sx={{ mb: 6 }}>
        <Alert severity="warning" sx={{ fontSize: '1rem' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <VolunteerActivism sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                🚨 WE URGENTLY NEED VOLUNTEERS!
              </Typography>
              <Typography variant="body1" paragraph>
                The <strong>Foodbarn</strong> needs volunteers! Any age welcome. Do you need to get out and feel great about yourself? 
                Come and help the team making a difference in thousands of lives.
              </Typography>
              <Typography variant="body2" paragraph>
                We are a registered workplace for Community Services, Centrelink & SPER.
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={handleNavigateToVolunteer}
                sx={{ mt: 1 }}
              >
                Sign Up as a Volunteer Today!
              </Button>
            </Box>
          </Box>
        </Alert>
      </Box>

      {/* Contact Information */}
      <ContactTiles title="📞 Get In Touch" />
    </Container>
  );
};

export default SponsorsPage; 